import type { SelectProps } from "@mui/material"
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import { CloseFilledOutlined, MoreHorizOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { FIELD_ERROR_CLASSES, FIELD_LABEL_CLASSES, SELECT_STYLING_PROPS } from "@/constants/forms"
import type { Permission } from "@/graphql/codegen/graphql"
import { useGetPermissions } from "@/screens/SystemAdmin/hooks"

const RoleForm: FC<{ isEditMode: boolean }> = ({ isEditMode }) => {
  const { control, watch } = useFormContext()
  const { permissions, fetching } = useGetPermissions()

  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)

  const selectedPermissionIds = watch("permissionIds")

  const [visibleChips, setVisibleChips] = useState<Permission[]>([])
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false)

  const selectedItems = useMemo(
    () => permissions.filter((el) => selectedPermissionIds.includes(el.permissionId)),
    [selectedPermissionIds, permissions]
  )
  const isPermissionsLoading = selectedItems.length === 0 && isEditMode && !fetching && selectedPermissionIds.length > 0

  useEffect(() => {
    if (measureRef.current && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth - 40 // 32 is the width of the MoreHorizOutlined icon
      let totalWidth = 0
      const visibleChips: Permission[] = []

      Array.from(measureRef.current.children).forEach((chip, index) => {
        totalWidth += (chip as HTMLElement).offsetWidth

        if (totalWidth <= containerWidth) {
          visibleChips.push(selectedItems[index])
        }
      })
      setVisibleChips(visibleChips)
      setIsOverflowing(visibleChips.length < selectedPermissionIds.length)
    }
  }, [selectedItems, selectedPermissionIds])

  const renderValues = (value: string[]) => {
    if (!value.length) {
      return <span color='text-gray-500'>Select At Least One</span>
    }
    return (
      <Box className='flex items-center overflow-hidden' ref={containerRef}>
        <Box className='flex flex-nowrap gap-1'>
          {visibleChips?.map((permission) => (
            <Chip
              key={permission.permissionId}
              label={permission.label}
              className='mr-1 h-6 rounded-sm bg-tertiary-yellow-300 text-xs text-gray-900'
            />
          ))}
        </Box>

        {isOverflowing && (
          <Box className='mx-1 flex size-6 items-center justify-center rounded-sm bg-tertiary-yellow-300'>
            <MoreHorizOutlined className='ml-0 shrink-0' />
          </Box>
        )}
      </Box>
    )
  }

  const handleDelete = (field: { value: string[]; onChange: (value: string[]) => void }, permissionId: string) => {
    field.onChange(field.value.filter((ids) => ids !== permissionId))
  }

  return (
    <form>
      <Controller
        name='name'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box data-testid='role-form-name'>
            <label htmlFor='name' className={FIELD_LABEL_CLASSES}>
              Role Name<span className='text-red-600'>*</span>
            </label>
            <TextField {...field} id='name' placeholder='Input Name' error={!!error} fullWidth variant='outlined' />
            {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </Box>
        )}
      />

      <Controller
        name='permissionIds'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box display='flex' flexDirection='column' data-testid='role-form-permission-ids'>
            <Box className='mt-8'>
              <Typography variant='body1' fontWeight={700} className='leading-6 tracking-[0.15px] text-gray-700'>
                Associated Permissions
              </Typography>
            </Box>

            <Box className='mt-4'>
              {isPermissionsLoading ? (
                <CircularProgress />
              ) : (
                <Box className='flex flex-wrap gap-2'>
                  {selectedItems?.map((permission) => (
                    <Chip
                      key={permission.permissionId}
                      label={permission.label}
                      className='h-6 rounded-sm bg-tertiary-yellow-300 text-xs text-gray-900'
                      onDelete={() => handleDelete(field, permission.permissionId)}
                      deleteIcon={<CloseFilledOutlined className='size-4 text-gray-900' />}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <FormControl fullWidth className='mb-2 mt-4'>
              <label htmlFor='role-permissions-select' className={FIELD_LABEL_CLASSES}>
                Role permissions<span className='text-red-600'>*</span>
              </label>
              <Select
                {...(SELECT_STYLING_PROPS as Partial<SelectProps<string[]>>)}
                id='role-permissions-select'
                multiple
                name={field.name}
                error={!!error}
                renderValue={renderValues}
                value={field.value ?? []}
                onChange={field.onChange}
              >
                {permissions.map(({ permissionId, label }) => (
                  <MenuItem key={permissionId} value={permissionId}>
                    <Checkbox checked={field.value?.map(String).includes(permissionId)} />
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box className='invisible absolute' ref={measureRef}>
              {selectedItems.map((permission) => (
                <Chip
                  key={`hidden-${permission.permissionId}`}
                  label={permission.label}
                  className='mr-1 h-6 rounded-sm bg-tertiary-yellow-300 text-xs text-gray-900'
                />
              ))}
            </Box>
            {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
          </Box>
        )}
      />
    </form>
  )
}

export default RoleForm
